import { useState, useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Icon, InputAdornment, TextField } from "@mui/material";

//components
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import { NotificationManager } from 'Components/Notifications';

//Helpers
import { copyToClipboard } from 'Core/data/Helpers';

//Redux
import { getAccountApiKeys, getApiKeyValue } from './redux/slice';

const maskLast4 = (last4) => !last4 ? '' : `••••••••${last4}`;

const MaskedApiKeyCell = ({ apiKeyId, last4, messages }) => {
    const dispatch = useDispatch();
    const [hovering, setHovering] = useState(false);
    const [copying, setCopying] = useState(false);

    const handleCopy = async () => {
        setCopying(true);
        try {
            const value = await dispatch(getApiKeyValue({ apiKeyId })).unwrap();
            copyToClipboard(value, messages['common_copied']);
        } catch (error) {
            NotificationManager.error(messages['account_api_keys_tab_copy_error'], messages['account_overview_api_keys'], 5000);
        } finally {
            setCopying(false);
        }
    };

    return (
        <div
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
        >
            <span style={{ fontFamily: 'monospace' }}>{maskLast4(last4)}</span>
            <Icon
                style={{
                    fontSize: 16,
                    cursor: copying ? 'default' : 'pointer',
                    visibility: hovering ? 'visible' : 'hidden',
                    opacity: hovering && !copying ? 1 : 0.5
                }}
                onClick={(!hovering || copying) ? undefined : handleCopy}
            >
                content_copy
            </Icon>
        </div>
    );
};

const ApiKeysTab = (props) => {
    const { company, height } = props;
    const { messages } = useIntl();
    const dispatch = useDispatch();

    const { items: apiKeys, loading } = useSelector(state => state.tabApiKeysRedux);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const fetchApiKeys = () => {
        if (!company?.id) return;
        dispatch(getAccountApiKeys({ accountId: company.id }));
    };

    useEffect(() => {
        fetchApiKeys();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company?.id]);

    // Debounce the actual filter so the grid's data array doesn't change on every
    // keystroke (rapid array-size changes, e.g. while backspacing, caused blank
    // gaps to flash in the virtualized grid) — typing itself stays instant.
    useEffect(() => {
        const timer = setTimeout(() => setSearchTerm(searchInput), 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const filteredApiKeys = useMemo(() => {
        if (!searchTerm) return apiKeys;
        const term = searchTerm.toLowerCase();
        return apiKeys.filter((item) => item?.apiKeyLabel?.toLowerCase().includes(term));
    }, [apiKeys, searchTerm]);

    const columns = useMemo(() => [
        {
            key: "apiKeyLabel",
            field: "apiKeyLabel",
            title: messages['account_api_keys_tab_name'],
            show: true,
            export: false,
            render: (item) => <span>{item?.apiKeyLabel}</span>
        },
        {
            key: "apiKeyLast4",
            field: "apiKeyLast4",
            title: messages['account_api_keys_tab_api_key'],
            show: true,
            export: false,
            render: (item) => <MaskedApiKeyCell apiKeyId={item?.apiKeyId} last4={item?.apiKeyLast4} messages={messages} />
        },
        {
            key: "rateLimitPlan",
            field: "rateLimit.usagePlanName",
            title: messages['account_api_keys_tab_rate_limit_plan'],
            show: true,
            export: false,
            render: (item) => <span>{item?.rateLimit?.usagePlanName || '—'}</span>
        },
        {
            key: "rateLimit",
            field: "rateLimit.quotaLimit",
            title: messages['account_api_keys_tab_rate_limit'],
            show: true,
            export: false,
            render: (item) => {
                const rl = item?.rateLimit;
                if (!rl?.quotaLimit) return <span>—</span>;
                return <span>{rl.quotaLimit}/{rl.quotaPeriod}</span>;
            }
        },
    ], [messages]);

    return (
        <div style={{ marginTop: 10 }}>
            <DataViewInfiniteScroll
                columns={columns}
                name="pm-account-api-keys-tab"
                infiniteScroll={false}
                externalDataConfig={{
                    data: filteredApiKeys,
                    loading,
                    totalCount: filteredApiKeys.length,
                    onRefresh: fetchApiKeys,
                }}
                searchConfig={{ disabled: true }}
                extraButton={() => (
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder={messages['account_api_keys_tab_search_placeholder']}
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Icon style={{ fontSize: 18 }}>search</Icon>
                                </InputAdornment>
                            )
                        }}
                    />
                )}
                uiConfig={{
                    hideFilters: true,
                    hideSplit: true,
                    hidePagination: true,
                    height: (height && height + "px") || "500px",
                }}
                selectionConfig={{ disabled: true }}
            />
        </div>
    );
};

export default ApiKeysTab;
